import axios, { AxiosError, AxiosHeaders, AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios"
import { toast } from "../utils/toast"
import { get, isArray } from "lodash"
import Cookies from "universal-cookie"
import { ErrorLogger } from "../ErrorLogger"
import { getAxiosErrorMsg, getPatientTenant } from "../utils/helpers"
import LoginExpiry from "@curefit/pandora/dist/components/loginBoundary/loginExpiry.js"
import { v4 as uuidV4 } from "uuid"
import { createLoggedAxiosInstance } from "./requestLogger"

const cookies = new Cookies()

export const API_BASE_URL = ""

export const triggerUserLogin = () => {
	const callbackPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
	const encodedCallbackPath = encodeURIComponent(callbackPath)
	// sf-prod: bypass the pandora LoginExpiry popup. accounts.sugarfit.org
	// 302s the popup back to its original callback URL (not to a /resume
	// page that posts a message to opener), so the opener tab is left
	// stranded with the modal still up. Navigate the current tab through
	// the synapse /login route, which 302s to accounts in the same tab.
	if (window.location.hostname === "synapse.sugarfit.org") {
		window.location.href = `/login?next=${encodedCallbackPath}`
		return
	}
	// cf-prod keeps the legacy popup-based flow (works against accounts.curefit.co).
	LoginExpiry.show({
		title: "Login Expired",
		description: "Login expired, you need to login again to continue.",
	})
}

export type Method = "get" | "GET" | "delete" | "DELETE" | "head" | "HEAD" | "options" | "OPTIONS" | "post" | "POST" | "put" | "PUT" | "patch" | "PATCH"

class ApiUtils {
	static namespace = new URL(window.location.href).searchParams.get("namespace") || "ODIN"

	static axiosInstance = createLoggedAxiosInstance();

	static get = (route: string, headers?: Record<string, any>, baseURL = API_BASE_URL, showError = true, toastDuration = 5, timeout = 60000): Promise<any> => {
		const allHeaders = ApiUtils.getAllHeaders(headers)
		return this.xhr(route, allHeaders, {}, "get", baseURL, false, undefined, 0, timeout, toastDuration, showError)
	}

	static post = (route: string, params: Record<string, any>, headers?: any, baseURL = API_BASE_URL, timeout?: number, showError = false, toastDuration = 5, maxRetries = 0): Promise<any> => {
		const allHeaders = ApiUtils.getAllHeaders(headers)
		return this.xhr(route, allHeaders, params, "post", baseURL, false, undefined, maxRetries, timeout, toastDuration, showError)
	}

	static put = (
		route: string,
		params: Record<string, any>,
		headers?: AxiosHeaders,
		onUploadProgress = (progressEvent: any) => {
			return
		},
		stringify?: boolean,
		baseURL = API_BASE_URL,
		maxRetries = 0,
		showError = false,
		toastDuration = 5,
	): Promise<any> => {
		const allHeaders = ApiUtils.getAllHeaders(headers)
		return ApiUtils.xhr(route, allHeaders, params, "put", baseURL, stringify, onUploadProgress, maxRetries, null, toastDuration, showError)
	}

	static patch = (route: string, params: Record<string, any>, headers?: AxiosHeaders, stringify?: boolean, baseURL = API_BASE_URL, showError = false): Promise<any> => {
		const allHeaders = ApiUtils.getAllHeaders(headers)
		return ApiUtils.xhr(route, allHeaders, params, "patch", baseURL, stringify, null, 0, null, null, showError)
	}

	static delete = (route: string, params: Record<string, any>, headers?: AxiosHeaders, baseURL = API_BASE_URL, showError = false): Promise<any> => {
		const allHeaders = ApiUtils.getAllHeaders(headers)
		return ApiUtils.xhr(route, allHeaders, params, "delete", baseURL, false, null, 0, null, null, showError)
	}

	static formData(url: string, data: Blob, fileMimeType: string, cb?: (loaded: number, total: number) => void) {
		return new Promise((resolve: any, reject: any) => {
			const request = new XMLHttpRequest()
			request.open("PUT", url, true)
			request.setRequestHeader("Content-Type", fileMimeType)
			request.setRequestHeader("x_tenant", getPatientTenant())

			request.upload.addEventListener("progress", (event: any) => {
				console.log({ loaded: event.loaded, total: event.total })
				cb && cb(event.loaded, event.total)
				if (event.lengthComputable) {
					// empty
				}
			})

			request.addEventListener("load", () => {
				resolve(request.responseText)
			})

			request.addEventListener("error", (event) => {
				console.log({ event: request.responseText })
				reject()
			})

			request.addEventListener("abort", (event) => {
				console.log({ abort: request.responseText })
				reject()
			})

			request.send(data)
		})
	}
	static xhr = (
		route: string,
		headers: AxiosHeaders,
		params: Record<string, unknown>,
		method: Method,
		baseURL = API_BASE_URL,
		stringify = false,
		onUploadProgress = (progressEvent: any) => {
			return
		},
		maxRetries = 0,
		timeout = 30000, // 30 seconds
		toastDuration = 5,
		showError = false
	): any => {
		// file object can't be stringified hence using a flag to determine.
		const data = params && stringify ? JSON.stringify(params) : params
		const controller = new AbortController()
		const signal = controller.signal
		const axiosConfig = {
			method,
			data,
			headers: {
				"x-synapse-request-id": uuidV4(),
				...(headers || {}),
			},
			baseURL,
			url: `${route}`,
			timeout,
			withCredentials: false,
			onUploadProgress,
			signal,
		} as AxiosRequestConfig
		return this.axiosInstance(axiosConfig)
			.then((resp) => {
				return resp?.data
			})
			.catch((error: any) => {
				if (signal?.aborted) {
					return
				}
				ApiUtils.axiosError(error, toastDuration, showError)
			})
	}

	static axiosError = (error: AxiosError, toastDuration = 5, showErrorMessage = false): void => {
		// ECONNABORTED + ERR_CANCELED are aborts/cancellations (background-tab freeze,
		// AbortController.abort, navigation). With clarifyTimeoutError enabled in
		// requestLogger.ts, genuine server timeouts arrive as ETIMEDOUT and continue
		// to propagate; this short-circuit only suppresses non-actionable aborts.
		const shouldIgnoreError =
			error?.code === "ERR_NETWORK" ||
			error?.code === "ECONNABORTED" ||
			error?.code === "ERR_CANCELED" ||
			error?.code === "AbortError" ||
			error?.name === "AbortError" ||
			error?.code === "ERR_CONNECTION_REFUSED"
		if (shouldIgnoreError) {
			return
		}
		if (error?.config?.baseURL === API_BASE_URL && error?.response?.status === 401) {
			/**
			 *  trigger user login
			 * */
			triggerUserLogin()
		} else if (get(error, "response.status") === 400) {
			if (typeof get(error, "response.data") === "string") {
				showErrorMessage &&
					toast.error(get(error, "response.data", "Invalid Request") as string, getAxiosErrorMsg(error) || error?.message, {
						duration: toastDuration,
					})
			} else {
				showErrorMessage &&
					toast.error(get(error, "response.data.title", "Something went wrong"), getAxiosErrorMsg(error) || error?.message, {
						duration: toastDuration,
					})
			}
		} else if (get(error, "response.status") === 404) {
			return
		} else {
			showErrorMessage &&
				toast.error(get(error, "response.data.title", "Something went wrong"), getAxiosErrorMsg(error) || error?.message, {
					duration: toastDuration,
				})
		}
		throw error
	}

	static getAllHeaders = (headers: any, tenant?: string): AxiosHeaders => {
		return {
			"X-User-Id": get(cookies.get("userContext"), "email", ""),
			"X-NAMESPACE": "synapse",
			"x-tenant-id": tenant ? tenant : "SUGARFIT",
			X_TENANT: tenant ? tenant : "SUGARFIT",
			X_USER_ID: get(cookies.get("userContext"), "email", ""),
			x_user_type: "ORION_USER",
			...headers,
		}
	}

	static requestToQueryParams(reqParams: any, hyphenated = false): string {
	    const currQueryParams: string[] = [];
	    for (const key in reqParams) {
	        if (Object(reqParams).hasOwnProperty(key) && reqParams[key] != null) {
	            const rawValue = reqParams[key];
	            let changedKey = key;
	            if (hyphenated) {
	                changedKey = key
	                    .replace(/[\w]([A-Z])/g, m => m[0] + '-' + m[1])
	                    .toLowerCase();
	            }
	            // Handle array values by emitting multiple entries
	            if (Array.isArray(rawValue)) {
	                rawValue.forEach(val => {
	                    currQueryParams.push(
	                        `${encodeURIComponent(changedKey)}=${encodeURIComponent(val)}`
	                    );
	                });
	            } else {
	                currQueryParams.push(
	                    `${encodeURIComponent(changedKey)}=${encodeURIComponent(rawValue)}`
	                );
	            }
	        }
	    }
	    return currQueryParams.join('&');
	}

	public static getOctetStreamAndGenerateFilePreview = (url: string, fileType?: string, requestHeaders?: any) => {
		return new Promise((resolve, reject) => {
			try {
				fetch(url, {
					method: "GET",
				})
					.then((response: any) => response.blob())
					.then((blob: any) => {
						const file = new Blob([blob], { type: fileType })
						const fileUrl = URL.createObjectURL(file)
						resolve({ fileUrl })
					})
					.catch((err) => {
						reject({ errorMsg: `Error in fetching data: ${url}`, error: err })
					})
			} catch (err) {
				reject({ errorMsg: `Error in fetching data: ${url}`, error: err })
			}
		})
	}

	public static apiFormDataPostRequest(url: string, formDataReq: Record<string, any>): Promise<any> {
		return new Promise((resolve: any, reject: any) => {
			const formData = new FormData()
			for (const key in formDataReq) {
				if (isArray(formDataReq[key])) {
					for (const data of formDataReq[key]) {
						formData.append(key, data)
					}
				} else formData.append(key, formDataReq[key])
			}

			fetch(url, {
				credentials: "same-origin",
				headers: { x_tenant: getPatientTenant() },
				method: "POST",
				body: formData,
			})
				.then((response: any) => {
					if (response.status >= 200 && response.status < 300) {
						resolve()
						// if (response) {
						// 	response.text().then((data: any) => {
						// 		if (data) {
						// 			resolve(JSON.parse(data))
						// 		} else {
						// 			resolve()
						// 		}
						// 	})
						// }
					} else {
						console.log("Error code 200 expected, while received: " + response.status)
						reject({ errorMsg: "Error code 200 expected, while received: " + response.status })
					}
				})
				.catch((error: any) => {
					console.log("Error while hitting POST request on URL: " + url, error)
					reject({ errorMsg: "Error while hitting POST request on URL: " + url, error })
				})
		})
	}
}

export default ApiUtils
